import { createRouter, createWebHistory } from 'vue-router'
import { getUser, isLoggedIn } from '../utils/auth'

const Home = () => import('../views/Home.vue')
const Orders = () => import('../views/Orders.vue')
const Common = () => import('../views/Common.vue')
const TaskList = () => import('../views/TaskList.vue')
const Invoice = () => import('../views/Invoice.vue')
const OrderDetail = () => import('../views/OrderDetail.vue')
const AddPassenger = () => import('../views/AddPassenger.vue')
const AddAddress = () => import('../views/AddAddress.vue')
const InvoiceDetail = () => import('../views/InvoiceDetail.vue')
const OrderInfo = () => import('../views/OrderInfo.vue')
const InvoiceAdd = () => import('../views/InvoiceAdd.vue')
const InvoiceProfiles = () => import('../views/InvoiceProfiles.vue')
const Profile = () => import('../views/Profile.vue')
const Login = () => import('../views/Login.vue')
const BindPhone = () => import('../views/BindPhone.vue')
const WechatAuth = () => import('../views/WechatAuth.vue')
const Privacy = () => import('../views/Privacy.vue')
const About = () => import('../views/About.vue')
const OrderChat = () => import('../views/OrderChat.vue')
const DriverProfileForm = () => import('../views/DriverProfileForm.vue')
const DriverHome = () => import('../views/DriverHome.vue')
const DriverOrders = () => import('../views/DriverOrders.vue')
const DriverOrderInfo = () => import('../views/DriverOrderInfo.vue')

const USER_LOGIN_PATH = '/user-login'
const DRIVER_LOGIN_PATH = '/driver-login'
const PUBLIC_PATHS = ['/login', USER_LOGIN_PATH, DRIVER_LOGIN_PATH, '/wechat-auth', '/privacy', '/about', '/bind-phone', '/profile']

// 返回当前登录用户是否为司机账号。
function isDriverUser() {
    return Number(getUser()?.user_type) === 2
}

// 返回账号类型对应的登录页。
function getLoginPathByUserType(userType) {
    return Number(userType) === 2 ? DRIVER_LOGIN_PATH : USER_LOGIN_PATH
}

// 返回当前路由是否属于乘客端页面。
function isPatientRoute(to) {
    return !to.meta.allowAnyUserType && !to.meta.requiresDriver && !to.meta.isLogin && !PUBLIC_PATHS.includes(to.path)
}

// 生成跨端访问时跳转到目标登录页的路由对象。
function buildLoginRedirect(path, redirect) {
    return {
        path,
        query: {
            redirect
        }
    }
}

const routes = [
    {
        path: '/',
        name: 'Home',
        component: Home
    },
    {
        path: '/orders',
        name: 'Orders',
        component: Orders
    },
    {
        path: '/common',
        name: 'Common',
        component: Common
    },
    {
        path: '/profile',
        name: 'Profile',
        component: Profile
    },
    {
        path: '/login',
        name: 'Login',
        redirect: to => {
            const { user_type, ...query } = to.query

            return {
                path: getLoginPathByUserType(user_type),
                query
            }
        }
    },
    {
        path: USER_LOGIN_PATH,
        name: 'UserLogin',
        component: Login,
        meta: {
            hideTabbar: true,
            isLogin: true,
            userType: 1
        }
    },
    {
        path: DRIVER_LOGIN_PATH,
        name: 'DriverLogin',
        component: Login,
        meta: {
            hideTabbar: true,
            isLogin: true,
            userType: 2
        }
    },
    {
        path: '/bind-phone',
        name: 'BindPhone',
        component: BindPhone,
        meta: {
            requiresAuth: true,
            hideTabbar: true
        }
    },
    {
        path: '/wechat-auth',
        name: 'WechatAuth',
        component: WechatAuth,
        meta: {
            hideTabbar: true
        }
    },
    {
        path: '/privacy',
        name: 'Privacy',
        component: Privacy,
        meta: {
            hideTabbar: true
        }
    },
    {
        path: '/about',
        name: 'About',
        component: About,
        meta: {
            hideTabbar: true
        }
    },
    {
        path: '/task-list',
        name: 'TaskList',
        component: TaskList
    },
    {
        path: '/invoice',
        name: 'Invoice',
        component: Invoice
    },
    {
        path: '/invoice-add',
        name: 'InvoiceAdd',
        component: InvoiceAdd
    },
    {
        path: '/invoice-profiles',
        name: 'InvoiceProfiles',
        component: InvoiceProfiles
    },
    {
        path: '/order-detail',
        name: 'OrderDetail',
        component: OrderDetail
    },
    {
        path: '/add-passenger',
        name: 'AddPassenger',
        component: AddPassenger
    },
    {
        path: '/add-address',
        name: 'AddAddress',
        component: AddAddress
    },
    {
        path: '/invoice-detail',
        name: 'InvoiceDetail',
        component: InvoiceDetail
    },
    {
        path: '/order-info',
        name: 'OrderInfo',
        component: OrderInfo
    },
    {
        path: '/order-chat',
        name: 'OrderChat',
        component: OrderChat,
        meta: {
            hideTabbar: true,
            allowAnyUserType: true
        }
    },
    {
        path: '/driver-profile-form',
        name: 'DriverProfileForm',
        component: DriverProfileForm,
        meta: {
            requiresDriver: true,
            hideTabbar: true
        }
    },
    {
        path: '/driver-home',
        name: 'DriverHome',
        component: DriverHome,
        meta: {
            requiresDriver: true
        }
    },
    {
        path: '/driver-orders',
        name: 'DriverOrders',
        redirect: to => ({
            path: to.query.tab === 'my' ? '/driver-my-tasks' : '/driver-grab-pool'
        }),
        meta: {
            requiresDriver: true
        }
    },
    {
        path: '/driver-grab-pool',
        name: 'DriverGrabPool',
        component: DriverOrders,
        meta: {
            requiresDriver: true,
            driverOrdersTab: 'pool'
        }
    },
    {
        path: '/driver-my-tasks',
        name: 'DriverMyTasks',
        component: DriverOrders,
        meta: {
            requiresDriver: true,
            driverOrdersTab: 'my'
        }
    },
    {
        path: '/driver-order-info',
        name: 'DriverOrderInfo',
        component: DriverOrderInfo,
        meta: {
            requiresDriver: true,
            hideTabbar: true
        }
    }
]

const router = createRouter({
    // history: createWebHistory('/firstAid/'),
    history: createWebHistory('/'),
    // 路由切换后统一将页面滚动到顶部。
    scrollBehavior() {
        return {
            left: 0,
            top: 0
        }
    },
    routes
})

// 根据登录态和账号类型处理全局路由跳转。
router.beforeEach((to, from, next) => {
    const loggedIn = isLoggedIn()
    const driverUser = isDriverUser()

    if (to.meta.requiresAuth && !loggedIn) {
        next(USER_LOGIN_PATH)
        return
    }

    if (to.meta.requiresDriver) {
        if (!loggedIn) {
            next(buildLoginRedirect(DRIVER_LOGIN_PATH, to.fullPath))
            return
        }

        if (!driverUser) {
            next(buildLoginRedirect(DRIVER_LOGIN_PATH, to.fullPath))
            return
        }
    }

    if (loggedIn && driverUser && isPatientRoute(to)) {
        next(buildLoginRedirect(USER_LOGIN_PATH, to.fullPath))
        return
    }

    next()
})

export default router
